import { useCallback } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Shield } from 'lucide-react';
import {
  fetchControlTower,
  type ControlTowerCriticalPathCheck,
  type ControlTowerResponse,
} from '@/services/api/controlTower';

type SystemStatus = 'checking' | 'good' | 'warn' | 'bad' | 'unknown';

const STATUS_META: Record<
  SystemStatus,
  { label: string; dotClassName: string; textClassName: string; title: string }
> = {
  checking: {
    label: 'Checking',
    dotClassName: 'bg-[var(--text-muted)]',
    textClassName: 'text-[var(--text-muted)]',
    title: 'Checking system status',
  },
  good: {
    label: 'Good',
    dotClassName: 'bg-[var(--text-muted)]',
    textClassName: 'text-[var(--text-secondary)]',
    title: 'System healthy',
  },
  warn: {
    label: 'Review',
    dotClassName: 'bg-[var(--accent)] shadow-[0_0_10px_var(--glow-actionable)]',
    textClassName: 'text-[var(--accent)]',
    title: 'System needs review',
  },
  bad: {
    label: 'Issue',
    // Doctrine rule 9: the dot carries the severity color; the word stays
    // high-contrast (--error at 12px is only ~3.6:1 on this surface).
    dotClassName: 'bg-[var(--error)]',
    textClassName: 'text-[var(--text-primary)]',
    title: 'System issue detected',
  },
  unknown: {
    label: 'Status unavailable',
    dotClassName: 'bg-[var(--text-muted)]',
    textClassName: 'text-[var(--text-muted)]',
    title: "Couldn't reach the health check. Showing nothing rather than guessing.",
  },
};

function formatStaleAsOf(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

function isCriticalTrustCheck(check: ControlTowerCriticalPathCheck): boolean {
  return (check.severity ?? 'critical') === 'critical';
}

function deriveSystemStatus(
  data: ControlTowerResponse | undefined,
  isFetching: boolean,
): SystemStatus {
  if (!data) return isFetching ? 'checking' : 'unknown';

  const criticalChecks = data.critical_path_checks.filter(isCriticalTrustCheck);
  const criticalStatus = new Set(criticalChecks.map((check) => check.status));
  const advisoryChecks = data.critical_path_checks.filter(
    (check) => !isCriticalTrustCheck(check) && check.status !== 'ok',
  );
  const graphCounts = data.system_graph_summary.counts ?? {};
  const graphErrors = graphCounts.error ?? 0;
  const graphStale = graphCounts.stale ?? 0;

  if (
    criticalStatus.has('error') ||
    graphErrors > 0 ||
    data.system_graph_summary.error ||
    data.learning_summary.error ||
    data.trace_summary.error ||
    data.trace_summary.reachable === false
  ) {
    return 'bad';
  }

  if (
    criticalStatus.has('warn') ||
    graphStale > 0 ||
    advisoryChecks.length > 0 ||
    data.learning_summary.stale_inputs.length > 0
  ) {
    return 'warn';
  }

  if (criticalChecks.length > 0 || data.generated_at) return 'good';

  return 'unknown';
}

export function DataOpsStatusButton({ propertyId }: { propertyId?: string | null }) {
  const navigate = useNavigate();
  const { data, isFetching, isError } = useQuery<ControlTowerResponse>({
    queryKey: ['control-tower-overview', propertyId || '__auto__'],
    queryFn: () => fetchControlTower({ propertyId: propertyId || null, days: 7 }),
    staleTime: 30_000,
    refetchInterval: 60_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
  // When a refresh fails but a last-known-good cache exists, fetchControlTower
  // returns the cached payload with `client_fallback` set (it only throws when
  // there is NO cache). In that case we show the LKG's own verdict plus a quiet
  // "as of {time}" hint rather than flipping to "Status unavailable".
  const status = isError ? 'unknown' : deriveSystemStatus(data, isFetching);
  const meta = STATUS_META[status];
  const staleAsOf = data?.client_fallback ? formatStaleAsOf(data.generated_at) : null;
  const title = staleAsOf
    ? `${meta.title}. Last checked ${staleAsOf}; couldn't refresh just now. Open System`
    : `${meta.title}. Open System`;
  const openControlTower = useCallback(() => {
    navigate('/system');
  }, [navigate]);

  return (
    <button
      type="button"
      onClick={openControlTower}
      className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--surface)] border border-[var(--border-color)] text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:border-[var(--accent)] transition-colors"
      title={title}
    >
      <Shield className="w-4 h-4" />
      <span className="text-sm">System</span>
      <span className="inline-flex items-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${meta.dotClassName}`} aria-hidden="true" />
        <span className={`text-xs font-mono ${meta.textClassName}`}>{meta.label}</span>
        {staleAsOf && (
          <span className="text-[10px] font-mono text-[var(--text-muted)]">as of {staleAsOf}</span>
        )}
      </span>
    </button>
  );
}
